import { OutlineButton, PrimaryButton } from "@components/Buttons";
import { CustomTextarea } from "@components/Forms";
import { Breadcrumb, FileUploader } from "@components/UI";
import FullScreenDotLoader from "@components/UI/FullScreenDotLoader";
import queryKeys from "@configs/react-query/query-key";
import { showErrorToast, showSuccessToast } from "@configs/react-toastify/snackbar";
import { useFileGroupAPI } from "@hooks/useFileGroupAPI";
import { useMutationData } from "@hooks/useMutationData";
import { useTheme } from "@hooks/useTheme";
import { uppercase } from "@utils/common";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { updateDataInIndexedDb, STORES } from "@utils/indexedDb";
import { CHARACTER_LENGTH } from "@constants/index";

export default function FAQAdd() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { colors } = useTheme();
  const { uploadFilesWithCustomCallback } = useFileGroupAPI(0);

  // Form state
  const [formData, setFormData] = useState({
    question: "",
    answer: "",
  });

  // File upload state
  const [selectedFiles, setSelectedFiles] = useState([]);

  // Validation state
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isInitialMount = useRef(true);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    validateForm();
  }, [formData]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.question.trim()) {
      newErrors.question = t("bulletin.question-required");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const addFaq = useMutationData(
    queryKeys.addFaq,
    {
      method: "POST",
    },
    {
      onSuccess: async (response) => {
        await updateDataInIndexedDb(STORES.FAQS, response, response.faqSeq);
        showSuccessToast(t("bulletin.faq-add-success"));
        // Handle file upload if files are selected
        if (selectedFiles.length > 0) {
          const formDataForUpload = new FormData();
          formDataForUpload.append("targetSeq", response.faqSeq);
          formDataForUpload.append("targetTypeCd", "FAQ");
          formDataForUpload.append("fileGroupNo", response.fileGroupNo ? response.fileGroupNo : 0);

          const uploadFilesData = selectedFiles.reduce((acc, file, index) => {
            if (file.file instanceof File) {
              acc[`file_${index}`] = file.file;
            }
            return acc;
          }, {});

          Object.entries(uploadFilesData).forEach(([key, file]) => {
            formDataForUpload.append(key, file, file.name);
          });

          uploadFilesWithCustomCallback.mutate(formDataForUpload, {
            onSuccess: () => {
              showSuccessToast(t("common.files-upload-success"));
              navigate(`/faq/${response.faqSeq}`);
            },
            onError: (error) => {
              console.error("Error during upload data:", error);
              showErrorToast(t("common.files-upload-error"));
              navigate(`/faq/${response.faqSeq}`);
            },
          });
        } else {
          navigate(`/faq/${response.faqSeq}`);
        }
      },
      onError: (error) => {
        console.error("Error during create data:", error);
        showErrorToast(t("bulletin.faq-add-error"));
        setIsSubmitting(false);
      },
    },
  );

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    await addFaq.mutateAsync(formData);
  };

  const handleCancel = () => {
    navigate("/faq");
  };

  const isFormValid = formData.question.trim();

  return (
    <div data-testid="faq-add-page">
      {/* Header */}
      <div className={`${colors.bgSecondary} ${colors.borderPrimary}`} data-testid="faq-add-header">
        <div className="flex items-center space-x-4">
          <Breadcrumb
            data-testid="faq-add-breadcrumb"
            items={[{ label: t("bulletin.faq-list-title"), href: "/faq" }, { label: t("bulletin.add-new-faq") }]}
          />
        </div>

        <div className="flex items-center justify-between mt-4">
          <h1 className={`${colors.textPrimary} text-2xl font-bold`} data-testid="faq-add-page-title">
            {uppercase(t("bulletin.add-new-faq"))}
          </h1>

          <div className="flex items-center space-x-3" data-testid="faq-add-header-actions">
            <OutlineButton
              data-testid="faq-add-cancel-button"
              onClick={handleCancel}
              className={`rounded-lg border px-4 py-1 font-semibold focus:outline-none ${colors.textSecondary} ${colors.borderHoverPrimary} ${colors.textHoverActive}`}
            >
              {t("common.cancel-button")}
            </OutlineButton>

            <PrimaryButton data-testid="faq-add-save-button" onClick={handleSubmit} disabled={!isFormValid || addFaq.isPending}>
              {addFaq.isPending ? (
                <div className="flex items-center">
                  <div className={`mr-2 h-4 w-4 animate-spin rounded-full border-b-2 ${colors.borderOnPrimary}`}></div>
                  {t("common.saving")}
                </div>
              ) : (
                t("common.save-button")
              )}
            </PrimaryButton>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className={`mt-5 p-6 ${colors.bgPrimary} rounded-2xl`} data-testid="faq-add-form">
        <div className={`mx-auto max-w-[56rem]`}>
          <div>
            {/* FAQ Information Section */}
            <div className="mb-6" data-testid="faq-add-information-section">
              <h2
                className={`${colors.textPrimary} text-xl font-semibold pt-2 pb-6`}
                data-testid="faq-add-information-title"
              >
                {t("bulletin.faq-information")}
              </h2>

              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                {/* Question */}
                <div className="md:col-span-2">
                  <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`}>
                    {t("bulletin.question")} <span className={colors.textRequired}>*</span>
                  </label>
                  <CustomTextarea
                    data-testid="faq-add-question-textarea"
                    rows={4}
                    placeholder={t("bulletin.enter-question")}
                    value={formData.question}
                    onChange={(e) => handleInputChange("question", e.target.value)}
                    title={formData.question}
                    className={`w-full rounded-lg border text-sm font-medium focus:outline-none ${colors.bgInput} ${colors.borderPrimary} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary} resize-none`}
                    error={errors.question}
                    maxLength={CHARACTER_LENGTH.CONTENT}
                  />
                  <p className={`text-xs ${colors.textCharacterCount} mt-1`} data-testid="faq-add-question-textarea-char-count">
                    {(formData.question || "").length}/{CHARACTER_LENGTH.CONTENT}
                  </p>
                </div>

                {/* Answer */}
                <div className="md:col-span-2">
                  <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`}>
                    {t("bulletin.answer")}
                  </label>
                  <CustomTextarea
                    data-testid="faq-add-answer-textarea"
                    rows={4}
                    placeholder={t("bulletin.enter-answer")}
                    value={formData.answer}
                    onChange={(e) => handleInputChange("answer", e.target.value)}
                    title={formData.answer}
                    className={`w-full rounded-lg border text-sm font-medium focus:outline-none ${colors.bgInput} ${colors.borderPrimary} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary} resize-none`}
                    maxLength={CHARACTER_LENGTH.CONTENT}
                  />
                  <p className={`text-xs ${colors.textCharacterCount} mt-1`} data-testid="faq-add-answer-textarea-char-count">
                    {(formData.answer || "").length}/{CHARACTER_LENGTH.CONTENT}
                  </p>
                </div>
              </div>
            </div>

            {/* Documents Section */}
            <div className="mb-6" data-testid="faq-add-attachments-section">
              <h2
                className={`${colors.textPrimary} mb-2 text-lg font-semibold`}
                data-testid="faq-add-attachments-title"
              >
                {uppercase(t("bulletin.attachment"))}
              </h2>
              <FileUploader
                data-testid="faq-add-file-uploader"
                onFilesChange={(files) => setSelectedFiles(files)}
                selectedFiles={selectedFiles}
                multiple={true}
              />
            </div>
          </div>
        </div>
      </div>
      <FullScreenDotLoader
        loading={uploadFilesWithCustomCallback.isPending}
        text={
          uploadFilesWithCustomCallback.isPending
            ? t("bulletin.uploading-faq") || "Uploading FAQ file..."
            : t("bulletin.upload-faq-file") || "Uploading FAQ file..."
        }
        size={80}
        speedMultiplier={1.2}
      />
    </div>
  );
}
